import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { DISCLOSURE_LEAD, DISCLOSURE_POINTS, DISCLOSURE_TITLE } from '@shared/disclosure'
import { MarketingShell, DemoStartError } from '../components/Layout'
import { Banner, Card } from '../ui'
import { api } from '../lib/api'

/**
 * The pre-sale home for the transport disclosure.
 *
 * `DisclosureNotice` (in `components/Disclosure.tsx`) shows this same canonical copy, but only
 * after signup, at the moment someone tries to pair a number. A buyer evaluating Wami — and
 * especially one who has to answer to a HIPAA or FINRA auditor — needs to be able to read it
 * before they commit to a trial, not after. This page renders the same `@shared/disclosure`
 * text rather than a rewritten summary, for the same reason `DisclosureNotice` does: two
 * hand-maintained copies of a legal statement drift the first time one is edited.
 */
export function Trust() {
  const navigate = useNavigate()
  const startDemo = useMutation({
    mutationFn: api.startDemo,
    onSuccess: () => navigate({ to: '/app' }),
  })

  return (
    <MarketingShell>
      <section className="container section stack gap-5" style={{ paddingBlock: '56px 32px' }}>
        <div className="stack gap-3" style={{ maxWidth: '65ch' }}>
          <span className="eyebrow">Trust &amp; transport</span>
          <h1 className="display-small">{DISCLOSURE_TITLE}</h1>
          <p className="body-large text-muted">{DISCLOSURE_LEAD}</p>
        </div>
      </section>

      <section className="container" style={{ paddingBottom: '48px' }}>
        <div className="stack gap-4" style={{ maxWidth: '75ch' }}>
          {DISCLOSURE_POINTS.map((point) => (
            <Card key={point.heading} variant="outlined">
              <div className="stack gap-2">
                <h2 className="title-large">{point.heading}</h2>
                <p className="body-medium text-muted">{point.body}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section
        className="section"
        style={{ background: 'var(--md-surface-container-low)', borderBlock: '1px solid var(--md-outline-variant)' }}
      >
        <div className="container stack gap-4" style={{ maxWidth: '75ch' }}>
          <h2 className="headline-medium">For HIPAA and FINRA-regulated teams</h2>
          <Banner tone="warn">
            <div className="stack gap-2">
              <p className="body-medium">
                The transport underneath Wami is unofficial, for every workspace, regardless of
                industry — that does not change because your compliance program is stricter than
                most. Weigh it as a real fact, not a formality to click past.
              </p>
              <p className="body-medium">
                Wami's audit log, BAA, and WORM-archive features work as advertised: messages are
                captured, retained, and exportable the way a regulator expects. What none of that
                changes is the channel those messages travel over — a number connected to Wami can
                still be banned by WhatsApp without warning, and no compliance feature on our side
                prevents that. If your posture requires a transport Meta has actually reviewed and
                approved, that is the official WhatsApp Business API through an authorised
                provider, not Wami.
              </p>
              <p className="body-medium">
                Some regulated teams decide the record-keeping is what they needed and accept the
                transport risk on a number they could afford to lose. Others decide they cannot.
                Both are reasonable; the point of this page is that you make that call knowing
                what you are choosing, not after.
              </p>
            </div>
          </Banner>
        </div>
      </section>

      <section className="container section stack gap-4 center" style={{ textAlign: 'center', alignItems: 'center' }}>
        <h2 className="headline-medium" style={{ maxWidth: '24ch' }}>
          Still the right fit? See it running.
        </h2>
        <p className="body-medium text-muted" style={{ maxWidth: '52ch' }}>
          Nothing above changes what the product does well. Start a trial or look at a live demo
          before you decide.
        </p>
        <div className="row gap-3 wrap center">
          <Link to="/signup" className="btn btn-filled btn-large">
            Start 14-day free trial
          </Link>
          <button
            type="button"
            className="btn btn-outlined btn-large"
            onClick={() => startDemo.mutate()}
            disabled={startDemo.isPending}
          >
            {startDemo.isPending ? 'Loading demo…' : 'See a live demo'}
          </button>
        </div>
        {startDemo.isError && <DemoStartError error={startDemo.error} />}
      </section>
    </MarketingShell>
  )
}
